import { useMutation, useQuery } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type RefObject } from 'react';
import { useUnsavedValue } from '../lib/unsaved.tsx';
import {
  attachmentsTooLargeText,
  bodySchema,
  DOCUMENT_ATTACH_TEXTS,
  documentChoiceText,
  emailLanguageCodes,
  emailLanguages,
  MAIL_BODY_MAX,
  MAIL_SUBJECT_MAX,
  recipientSchema,
  subjectSchema,
  type EmailLanguage,
} from '@till-erp/mail/values';
import { apiFetch, ApiProblem, newIdempotencyKey, type MailCompose, type MailPreview } from '../lib/api.ts';
import { formatFileSize } from '../lib/format.ts';
import { useScreen } from '../lib/layout.ts';
import { useSession } from '../lib/session.tsx';
import { cn } from '../lib/utils.ts';
import { Button } from './ui/button.tsx';
import { Input } from './ui/input.tsx';
import { Label } from './ui/label.tsx';
import { Popover } from './ui/popover.tsx';
import { Segment } from './ui/segment.tsx';
import { Select } from './ui/select.tsx';
import { Textarea } from './ui/textarea.tsx';

type FieldErrors = Partial<Record<'to' | 'subject' | 'body' | 'attachments' | 'documents' | 'template', string>>;

function FieldError({ id, text }: { id: string; text: string | undefined }) {
  if (!text) return null;
  return (
    <p id={id} className="type-meta mt-space-2 text-clarify">
      {text}
    </p>
  );
}

function problemText(err: unknown): string {
  return err instanceof ApiProblem ? err.detail : 'Etwas ist schiefgelaufen. Bitte noch einmal versuchen.';
}

/** Erste Meldung einer Zod-Prüfung (dieselben Schemas wie der Server). */
function firstIssue(result: { success: boolean; error?: { issues: readonly { message: string }[] } }): string | undefined {
  return result.success ? undefined : result.error?.issues[0]?.message;
}

const NO_TEMPLATE = '';

/**
 * Vorlagen-Wahl am Handy (EXPERIENCE §10.2 „Dialog, Popover“): Knopf mit der gewählten Vorlage öffnet ein Blatt von
 * unten mit einer Liste von Knöpfen (44 px je Zeile, aktuelle Wahl mit Häkchen und `aria-pressed`); die Wahl schließt das Blatt, der Fokus kehrt auf den Knopf zurück.
 */
function TemplateSheet({
  labelId,
  templates,
  value,
  onChange,
}: {
  labelId: string;
  templates: MailCompose['templates'];
  value: string;
  onChange: (templateId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = [{ id: NO_TEMPLATE, name: 'Ohne Vorlage' }, ...templates.map((t) => ({ id: t.id, name: t.name }))];
  const current = options.find((o) => o.id === value)?.name ?? 'Ohne Vorlage';
  return (
    <div>
      <span id={labelId} className="type-meta mb-space-1 block text-ink-2">
        Vorlage
      </span>
      <Popover
        open={open}
        onOpenChange={setOpen}
        label="Vorlage wählen"
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-labelledby={`${labelId} ${labelId}-wert`}
            className="flex h-11 w-full cursor-pointer items-center rounded-field border border-line bg-card px-space-4 text-left text-card-title text-ink"
          >
            <span id={`${labelId}-wert`} className="min-w-0 flex-1 [overflow-wrap:anywhere]">
              {current}
            </span>
          </button>
        )}
      >
        {/* Knöpfe statt Radio-Gruppe: Pfeiltasten wählen nichts aus; gewählt wird nur per Tipp, Klick oder Enter. */}
        <ul aria-label="Vorlagen" className="m-0 flex list-none flex-col p-0">
          {options.map((o) => {
            const active = o.id === value;
            return (
              <li key={o.id || 'ohne'}>
                <button
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    if (!active) onChange(o.id);
                    setOpen(false);
                  }}
                  className="flex min-h-11 w-full cursor-pointer items-center gap-space-3 rounded-tile px-space-2 text-left text-lead text-ink hover:bg-tile"
                >
                  <span aria-hidden className="grid size-5 flex-none place-items-center text-brand">
                    {active ? <Check size={18} strokeWidth={2} /> : null}
                  </span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">{o.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </Popover>
    </div>
  );
}

/**
 * „Beleg anhängen“ (Story 3.10, AD-8): aufklappbare Liste der Belegfassungen des Datensatzes. Wählbar sind nur
 * versiegelte Fassungen (das archivierte PDF geht hinaus, nie neu erzeugt); die übrigen stehen ausgegraut mit Grund da.
 */
function DocumentPicker({
  documents,
  picked,
  onChange,
  error,
  errorId,
  summaryRef,
}: {
  documents: MailCompose['documents'];
  picked: string[];
  onChange: (next: string[]) => void;
  error: string | undefined;
  errorId: string;
  summaryRef: RefObject<HTMLElement | null>;
}) {
  const reasonId = useId();
  const [open, setOpen] = useState(false);
  return (
    // Fehler zu den Belegen (z. B. „wird noch erzeugt“): aufgeklappt, damit der Satz zu sehen ist.
    <details open={open || !!error} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary ref={summaryRef} aria-describedby={error ? errorId : undefined} className="type-meta flex min-h-11 cursor-pointer items-center text-ink-2 hover:underline">Beleg anhängen</summary>
      <fieldset aria-describedby={error ? errorId : undefined} className="mt-space-1">
        <legend className="sr-only">Belege des Datensatzes</legend>
        <ul className="flex list-none flex-col gap-space-1 p-0">
          {documents.flatMap((d) => {
            const rows = d.artifacts.length > 0 ? d.artifacts : [null];
            return rows.map((a, i) => {
              const label = `${documentChoiceText(d.number, d.versionNo)}${a && a.kind !== 'pdf' ? ` (${a.kind})` : ''}`;
              const disabled = !d.attachable || !a;
              const descId = `${reasonId}-${d.versionId}-${i}`;
              // Versiegelte Fassung ohne Artefakt: eigener Grund statt leerer Zeile.
              const reason = d.reason ?? (a ? null : DOCUMENT_ATTACH_TEXTS.noArtifact);
              return (
                <li key={a?.id ?? d.versionId}>
                  <label
                    className={cn(
                      'flex min-h-11 items-start gap-space-2 py-space-2 text-row [overflow-wrap:anywhere]',
                      disabled ? 'cursor-not-allowed text-ink-3' : 'cursor-pointer text-ink',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="mt-space-1 size-4 flex-none accent-[var(--accent-brand)]"
                      disabled={disabled}
                      aria-describedby={disabled && reason ? descId : undefined}
                      checked={!disabled && !!a && picked.includes(a.id)}
                      onChange={(e) => {
                        if (!a) return;
                        onChange(e.target.checked ? [...picked, a.id] : picked.filter((id) => id !== a.id));
                      }}
                    />
                    <span>
                      {label}
                      {a ? <span className="type-meta text-ink-3"> · {formatFileSize(a.sizeBytes)}</span> : null}
                      {disabled && reason ? (
                        <span id={descId} className="type-meta block text-ink-3">
                          {reason}
                        </span>
                      ) : null}
                    </span>
                  </label>
                </li>
              );
            });
          })}
        </ul>
        <p className="type-meta mt-space-1 text-ink-3">Es geht die archivierte Datei hinaus, unverändert.</p>
        <FieldError id={errorId} text={error} />
      </fieldset>
    </details>
  );
}

/**
 * E-Mail aus dem Datensatz (Story 2.7, FR-62, EXPERIENCE §4.1 Chatter-Spalte): Empfänger (Vorschläge des
 * Datensatzes), Vorlage, Sprache DE/EN, Betreff und Text (Platzhalter gefüllt), Dateien des Datensatzes als Anhang,
 * Belege des Datensatzes als Anhang (Story 3.10, archiviertes PDF).
 * Gesendet wird erst mit dem Hauptknopf „E-Mail senden an …“ (echte Bestätigung ohne Fenster, EXPERIENCE §6.4).
 */
export function MailComposer({
  entityType,
  entityId,
  onSent,
  onCancel,
}: {
  entityType: string;
  entityId: string;
  onSent: (to: string) => void;
  onCancel: () => void;
}) {
  const { getToken } = useSession();
  const phone = useScreen() === 'phone';
  const compose = useQuery({
    queryKey: ['mail-compose', entityType, entityId],
    queryFn: () => apiFetch<MailCompose>(getToken, `/api/v1/mail/compose/${entityType}/${encodeURIComponent(entityId)}`),
  });
  const data = compose.data;
  const [to, setTo] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState(NO_TEMPLATE);
  const [language, setLanguage] = useState<EmailLanguage | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [template, setTemplate] = useState<{ id: string; version: number } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [pickedDocs, setPickedDocs] = useState<string[]>([]);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [missingText, setMissingText] = useState<string | null>(null);
  const ids = {
    to: useId(),
    toErr: useId(),
    list: useId(),
    template: useId(),
    subject: useId(),
    subjectErr: useId(),
    body: useId(),
    bodyErr: useId(),
    files: useId(),
    filesErr: useId(),
    docsErr: useId(),
  };
  const toRef = useRef<HTMLInputElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const docsRef = useRef<HTMLElement>(null);

  // Vorschläge des Datensatzes übernehmen, sobald sie da sind (Muster „Zustand aus Eingabe“).
  const recipient = to ?? data?.recipients[0]?.address ?? '';
  const lang = language ?? data?.language ?? 'de';
  // Fokus einmal in „An“, sobald das Formular da ist (nicht bei jedem Nachladen, z. B. nach einer neuen Datei).
  const ready = data !== undefined;
  useEffect(() => {
    if (ready) toRef.current?.focus();
  }, [ready]);

  // Nur die Antwort zur zuletzt gewählten Vorlage und Sprache zählt (ältere oder fehlgeschlagene Vorschauen nie übernehmen).
  const previewSeq = useRef(0);
  const preview = useMutation({
    mutationFn: async (input: { templateId: string; language: EmailLanguage; seq: number }) => ({
      seq: input.seq,
      preview: await apiFetch<MailPreview>(
        getToken,
        `/api/v1/mail/compose/${entityType}/${encodeURIComponent(entityId)}/preview?templateId=${input.templateId}&language=${input.language}`,
      ),
    }),
    onSuccess: ({ seq, preview: p }) => {
      if (seq !== previewSeq.current) return;
      setSubject(p.subject);
      setBody(p.body);
      setTemplate({ id: p.template.id, version: p.template.version });
      setMissingText(p.missingText);
      setErrors({});
    },
    onError: (err, input) => {
      if (input.seq === previewSeq.current) setFormError(problemText(err));
    },
  });

  function choose(nextTemplate: string, nextLanguage: EmailLanguage) {
    setTemplateId(nextTemplate);
    setLanguage(nextLanguage);
    setFormError(null);
    // Vorlagenstand erst wieder setzen, wenn die passende Vorschau da ist; bis dahin kein Senden mit Vorlage.
    setTemplate(null);
    const seq = ++previewSeq.current;
    if (nextTemplate === NO_TEMPLATE) {
      setMissingText(null);
      return;
    }
    preview.mutate({ templateId: nextTemplate, language: nextLanguage, seq });
  }

  const files = data?.attachments ?? [];
  const docArtifacts = (data?.documents ?? []).flatMap((d) => (d.attachable ? d.artifacts : []));
  const pickedSize =
    files.filter((f) => picked.includes(f.id)).reduce((sum, f) => sum + f.sizeBytes, 0) +
    docArtifacts.filter((a) => pickedDocs.includes(a.id)).reduce((sum, a) => sum + a.sizeBytes, 0);
  // Nur Belege, die es nach dem letzten Laden noch wählbar gibt (Daten können sich neu laden).
  const sentDocs = pickedDocs.filter((id) => docArtifacts.some((a) => a.id === id));
  const pickedCount = picked.length + sentDocs.length;

  const send = useMutation({
    mutationFn: () =>
      apiFetch<{ mail: { id: string } }>(getToken, `/api/v1/mail/compose/${entityType}/${encodeURIComponent(entityId)}`, {
        method: 'POST',
        body: { to: recipient.trim(), language: lang, template, subject, body, attachmentIds: picked, documentArtifactIds: sentDocs },
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: () => onSent(recipient.trim()),
    onError: (err) => {
      if (err instanceof ApiProblem && Object.keys(err.errors).length > 0) {
        const next: FieldErrors = {};
        for (const key of ['to', 'subject', 'body', 'attachments', 'documents', 'template'] as const) if (err.errors[key]) next[key] = err.errors[key];
        if (Object.keys(next).length === 0) setFormError(err.detail);
        showErrors(next);
      } else setFormError(problemText(err));
    },
  });

  /** Fehler am Feld zeigen und den Fokus zum ersten Fehler setzen (EXPERIENCE §4.3). */
  function showErrors(next: FieldErrors) {
    setErrors(next);
    const first = next.to ? toRef.current : next.subject ? subjectRef.current : next.body ? bodyRef.current : next.documents ? docsRef.current : null;
    if (first) requestAnimationFrame(() => first.focus());
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    setFormError(null);
    setMissingText(null);
    // Vorschau der gewählten Vorlage steht noch aus (oder ist fehlgeschlagen): nicht mit altem Vorlagenstand senden.
    if (!data || (templateId !== NO_TEMPLATE && template === null)) return;
    const next: FieldErrors = {};
    const toIssue = firstIssue(recipientSchema.safeParse(recipient.trim()));
    if (toIssue) next.to = toIssue;
    const subjectIssue = firstIssue(subjectSchema.safeParse(subject));
    if (subjectIssue) next.subject = subjectIssue;
    const bodyIssue = firstIssue(bodySchema.safeParse(body.trim() ? body : ''));
    if (bodyIssue) next.body = bodyIssue;
    if (pickedSize > data.attachmentMaxBytes) next.attachments = attachmentsTooLargeText(data.attachmentMaxBytes);
    // Inzwischen nicht mehr anhängbar (neu geladen: ungültig, entfernt …): nie still weglassen, sondern abwählen und sagen.
    const stale = pickedDocs.filter((id) => !sentDocs.includes(id));
    if (stale.length > 0) {
      setPickedDocs(sentDocs);
      next.documents = DOCUMENT_ATTACH_TEXTS.staleChoice;
    }
    if (Object.keys(next).length > 0) {
      showErrors(next);
      return;
    }
    setErrors({});
    send.mutate();
  }

  // Kein Strg+Enter: Senden geht nur über den Knopf mit Zusammenfassung (echte Bestätigung, EXPERIENCE §6.4).
  // Esc schließt nur ohne Eingaben; mit Eingaben bleibt alles stehen (EXPERIENCE §4.3).
  // Ganzer Entwurf gegen den Stand beim Öffnen (nicht Feld für Feld). Zählt als ungespeichert, lässt sich aber nicht
  // speichern (Senden wäre Außenwirkung): ist nur er geändert, heißt der Knopf der Leiste „Weiter bearbeiten“.
  const dirty = useUnsavedValue({ to, templateId, language, subject, body, picked, pickedDocs, template }, null);
  function onKeyDown(e: KeyboardEvent<HTMLFormElement>) {
    if (e.key === 'Escape' && !dirty) {
      e.preventDefault();
      onCancel();
    }
  }

  if (compose.isPending) return <p className="type-meta mt-space-4 text-ink-3">Lädt …</p>;
  if (compose.error) return <p className="type-meta mt-space-4 text-clarify">{problemText(compose.error)}</p>;
  if (!data) return null;
  if (!data.available) {
    return (
      <div className="mt-space-4 rounded-tile bg-tile p-space-4">
        <p className="text-row text-ink">An diese Art von Datensatz kann man noch keine E-Mail schreiben.</p>
        <button type="button" onClick={onCancel} className="type-meta mt-space-2 min-h-6 cursor-pointer text-ink-2 hover:underline">
          Schließen
        </button>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={submit} onKeyDown={onKeyDown} aria-label="E-Mail schreiben" className="mt-space-4 flex flex-col gap-space-4 rounded-tile bg-tile p-space-4">
      <div>
        <Label htmlFor={ids.to} required>
          An
        </Label>
        <Input
          id={ids.to}
          ref={toRef}
          type="email"
          list={data.recipients.length > 0 ? ids.list : undefined}
          value={recipient}
          invalid={!!errors.to}
          aria-describedby={errors.to ? ids.toErr : undefined}
          placeholder="name@firma.de"
          onChange={(e) => setTo(e.target.value)}
        />
        {data.recipients.length > 0 ? (
          <datalist id={ids.list}>
            {data.recipients.map((r) => (
              <option key={r.address} value={r.address}>
                {r.label}
              </option>
            ))}
          </datalist>
        ) : null}
        <FieldError id={ids.toErr} text={errors.to} />
      </div>
      {phone ? (
        <TemplateSheet
          labelId={ids.template}
          templates={data.templates}
          value={templateId}
          onChange={(next) => choose(next, lang)}
        />
      ) : (
        <div>
          <Label htmlFor={ids.template}>Vorlage</Label>
          <Select id={ids.template} value={templateId} onChange={(e) => choose(e.target.value, lang)}>
            <option value={NO_TEMPLATE}>Ohne Vorlage</option>
            {data.templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div>
        <Segment
          label="Sprache"
          options={emailLanguages.map((l) => ({ value: l, label: l === 'de' ? `Deutsch (${emailLanguageCodes.de})` : `Englisch (${emailLanguageCodes.en})` }))}
          value={lang}
          onChange={(l) => choose(templateId, l)}
          dense
        />
      </div>
      <div>
        <Label htmlFor={ids.subject} required>
          Betreff
        </Label>
        <Input
          id={ids.subject}
          ref={subjectRef}
          value={subject}
          maxLength={MAIL_SUBJECT_MAX}
          invalid={!!errors.subject}
          aria-describedby={errors.subject ? ids.subjectErr : undefined}
          onChange={(e) => setSubject(e.target.value)}
        />
        <FieldError id={ids.subjectErr} text={errors.subject} />
      </div>
      <div>
        <Label htmlFor={ids.body} required>
          Text
        </Label>
        <Textarea
          id={ids.body}
          ref={bodyRef}
          value={body}
          rows={8}
          maxLength={MAIL_BODY_MAX}
          invalid={!!(errors.body || missingText)}
          aria-describedby={errors.body || missingText ? ids.bodyErr : undefined}
          onChange={(e) => setBody(e.target.value)}
          className="min-h-0"
        />
        <FieldError id={ids.bodyErr} text={errors.body ?? missingText ?? undefined} />
      </div>
      <fieldset aria-describedby={errors.attachments ? ids.filesErr : undefined}>
        <legend className="type-meta mb-space-2 text-ink-2">Dateien des Datensatzes anhängen</legend>
        {files.length === 0 ? (
          <p className="type-meta text-ink-3">Am Datensatz hängen noch keine Dateien („Datei anhängen“).</p>
        ) : (
          <ul className="flex list-none flex-col gap-space-1 p-0">
            {files.map((f) => (
              <li key={f.id}>
                <label className="flex min-h-6 cursor-pointer items-start gap-space-2 text-row text-ink [overflow-wrap:anywhere]">
                  <input
                    type="checkbox"
                    className="mt-space-1 size-4 flex-none accent-[var(--accent-brand)]"
                    checked={picked.includes(f.id)}
                    onChange={(e) => setPicked((prev) => (e.target.checked ? [...prev, f.id] : prev.filter((id) => id !== f.id)))}
                  />
                  <span>
                    {f.fileName} <span className="type-meta text-ink-3">· {formatFileSize(f.sizeBytes)}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
        )}
        <p className="type-meta mt-space-1 text-ink-3">Zusammen höchstens {formatFileSize(data.attachmentMaxBytes)}.</p>
        <FieldError id={ids.filesErr} text={errors.attachments} />
      </fieldset>
      {data.documents.length > 0 ? (
        <DocumentPicker documents={data.documents} picked={pickedDocs} onChange={setPickedDocs} error={errors.documents} errorId={ids.docsErr} summaryRef={docsRef} />
      ) : null}
      {formError || errors.template ? (
        <p role="alert" className="type-meta rounded-tile bg-clarify-bg px-space-4 py-space-3 text-clarify">
          {errors.template ?? formError}
        </p>
      ) : null}
      <div className="flex flex-col items-end gap-space-2">
        <Button
          type="submit"
          disabled={send.isPending || (templateId !== NO_TEMPLATE && template === null)}
          className="h-auto min-h-11 max-w-full py-space-2 text-center whitespace-normal [overflow-wrap:anywhere]"
        >
          {`E-Mail senden an ${recipient.trim() || '…'}`}
        </Button>
        <p className="type-meta text-right text-ink-3">
          {pickedCount > 0 ? `${pickedCount === 1 ? '1 Anhang' : `${pickedCount} Anhänge`} · ` : ''}Geht sofort raus und steht danach im Chatter.
        </p>
        <button type="button" onClick={onCancel} className="type-meta min-h-6 cursor-pointer text-ink-2 hover:underline">
          Abbrechen
        </button>
      </div>
    </form>
  );
}
